import { httpClient } from '@/httpClient';
import plans from '@/store/crm/plans/index';
import plan from '@/store/crm/plans/id';
import myPlans from '@/store/crm/my-plans';
import apartments from '@/store/crm/apartments';
import statistic from '@/store/crm/statistic';
import customers from '@/store/crm/customers';
import { api } from '@/api';

export default {
  namespaced: true,
  state: {
    currentPlan: null,
    isCurrentPlanLoading: false,
    statusHistory: {
      items: [],
      apartmentPauses: [],
      isLoading: false,
    },
    tagsList: [],
    simpleUsersList: [],
    isSimpleUsersListLoading: false,

    modals: {
      changeCutter: {
        isVisible: false,
        isLoading: false,
      },
      changeNinja: {
        isVisible: false,
        isLoading: false,
      },
      changeManager: {
        isVisible: false,
        isLoading: false,
      },
      changeReviewer: {
        isVisible: false,
        isLoading: false,
      },
    },

    adjustments: {
      items: [],
      isLoading: false,
    },
  },
  mutations: {
    setCurrentPlan(state, data) {
      state.currentPlan = data;
    },
    setStrategicData(state, {
      data,
    }) {
      state.currentPlan.region = data.region;
      state.currentPlan.developer = data.developer;
      state.currentPlan.complex = data.complex;
      state.currentPlan.address = data.address;
    },
    setStrategicDataRegion(state, data) {
      state.currentPlan.region = data;
    },
    setStrategicDataDeveloper(state, data) {
      state.currentPlan.developer = data;
    },
    setStrategicDataComplex(state, data) {
      state.currentPlan.complex = data;
    },
    setStrategicDataAddress(state, data) {
      state.currentPlan.address = data;
    },
    setTagsList(state, data) {
      state.tagsList = data;
    },
    setStatusHistory(state, { items, apartment_pauses: apartmentPauses = [] }) {
      state.statusHistory.items = items;
      state.statusHistory.apartmentPauses = apartmentPauses;
    },
    setStatusHistoryLoading(state, isLoading) {
      state.statusHistory.isLoading = isLoading;
    },
    getSimpleUsersList(state, data) {
      state.simpleUsersList = data;
    },
    setIsSimpleUsersListLoading(state, value) {
      state.isSimpleUsersListLoading = value;
    },
    setCurrentPlanLoading(state, isLoading) {
      state.isCurrentPlanLoading = isLoading;
    },
    setCurrentPlanName(state, name) {
      state.currentPlan.name = name;
    },
    setCurrentPlanDescription(state, description) {
      state.currentPlan.description = description;
    },
    setChangeNinjaModalVisibility(state, visibility) {
      state.modals.changeNinja.isVisible = visibility;
    },
    setChangeNinjaModalLoading(state, isLoading) {
      state.modals.changeNinja.isLoading = isLoading;
    },
    setCurrentPlanNinja(state, { id, name }) {
      state.currentPlan.ninja = { id, name };
    },
    setChangeCutterModalVisibility(state, visibility) {
      state.modals.changeCutter.isVisible = visibility;
    },
    setChangeCutterModalLoading(state, isLoading) {
      state.modals.changeNinja.isLoading = isLoading;
    },
    setCurrentPlanCutter(state, { id, name }) {
      state.currentPlan.cutter = { id, name };
    },
    setChangeManagerModalVisibility(state, visibility) {
      state.modals.changeManager.isVisible = visibility;
    },
    setChangeManagerModalLoading(state, isLoading) {
      state.modals.changeManager.isLoading = isLoading;
    },
    setCurrentPlanManager(state, { id, name }) {
      state.currentPlan.manager = { id, name };
    },
    setChangeReviewerModalVisibility(state, visibility) {
      state.modals.changeReviewer.isVisible = visibility;
    },
    setChangeReviewerModalLoading(state, isLoading) {
      state.modals.changeReviewer.isLoading = isLoading;
    },
    setCurrentPlanReviewer(state, user) {
      state.currentPlan.reviewer = user ? { id: user.id, name: user.name } : null;
    },
    setCurrentPlanDeadline(state, { deadlineAt, deadlineOverridden }) {
      state.currentPlan.deadline_at = deadlineAt;
      state.currentPlan.deadline_overridden = deadlineOverridden;
    },
    setAdjustments(state, items) {
      state.adjustments.items = items;
    },
    setAdjustmentsLoading(state, isLoading) {
      state.adjustments.isLoading = isLoading;
    },
  },
  getters: {
    simpleNinjasList: (state) => state.simpleUsersList,
    simpleReviewersList: (state) => state.simpleUsersList,
  },
  actions: {
    getCrmPlanById(store, id) {
      return httpClient.get(`/api/crm/plans/${id}/`).then((resp) => {
        const { data } = resp;
        store.commit('setCurrentPlan', data);
        return data;
      });
    },
    getCrmPlan3dById({ commit }, id) {
      commit('setCurrentPlanLoading', true);

      return httpClient.get(`/api/crm/plans/${id}/plan_3d`)
        .then((resp) => {
          commit('setCurrentPlan', resp.data);
        })
        .finally(() => commit('setCurrentPlanLoading', false));
    },
    changeStrategicData(store, {
      id, region, developer, complex, address,
    }) {
      return httpClient.post(`api/crm/plans/${id}/change_strategic_data`, {
        region, developer, complex, address,
      }).then(({ data }) => {
        store.commit('setStrategicData', {
          data,
        });
        return data;
      });
    },
    getCrmPlan3dByIdWithoutLoading({ commit }, id) {
      return httpClient.get(`/api/crm/plans/${id}/plan_3d`).then((resp) => commit('setCurrentPlan', resp.data));
    },
    changePlanName({ commit, state }, { id, name }) {
      const oldName = state.currentPlan.name;

      commit('setCurrentPlanName', name);

      return httpClient.post(`/api/crm/plans/${id}/rename`, { id, name })
        .then((resp) => resp.data)
        .catch(() => commit('setCurrentPlanName', oldName));
    },
    changePlanDescription({ commit, state }, { id, description }) {
      const oldDescription = state.currentPlan.description;

      commit('setCurrentPlanDescription', description);

      return httpClient.post(`api/crm/plans/${id}/change_description`, { id, description })
        .then((resp) => resp.data)
        .catch(() => commit('setCurrentPlanDescription', oldDescription));
    },
    changeCrmPlanNinja({ commit, state }, { id, ninjaId }) {
      commit('setChangeNinjaModalLoading', true);

      return httpClient.post(`/api/crm/plans/${id}/change_ninja`, { ninjaId })
        .then((resp) => {
          commit('setCurrentPlanNinja', state.simpleUsersList.find((item) => item.id === ninjaId));
          commit('setChangeNinjaModalVisibility', false);
          return resp.data;
        })
        .finally(() => {
          commit('setChangeNinjaModalLoading', false);
        });
    },
    changeCrmPlanNinja3d({ commit, state }, { id, ninjaId }) {
      commit('setChangeNinjaModalLoading', true);

      return httpClient.post(`/api/crm/plans/${id}/change_ninja_3d`, { ninjaId })
        .then((resp) => {
          commit('setCurrentPlanNinja', state.simpleUsersList.find((item) => item.id === ninjaId));
          commit('setChangeNinjaModalVisibility', false);
          return resp.data;
        })
        .finally(() => {
          commit('setChangeNinjaModalLoading', false);
        });
    },
    changeCrmPlanCutter({ commit, state }, { id, cutterId }) {
      commit('setChangeCutterModalLoading', true);

      return api.changeCutter(id, cutterId, false)
        .then((resp) => resp.data)
        .finally(() => {
          commit('setCurrentPlanCutter', state.simpleUsersList.find((item) => item.id === cutterId));
          commit('setChangeCutterModalLoading', false);
          commit('setChangeCutterModalVisibility', false);
        });
    },
    changeCrmPlanCutter3d({ commit, state }, { id, cutterId }) {
      commit('setChangeCutterModalLoading', true);

      return api.changeCutter(id, cutterId, true)
        .then((resp) => resp.data)
        .finally(() => {
          commit('setCurrentPlanCutter', state.simpleUsersList.find((item) => item.id === cutterId));
          commit('setChangeCutterModalLoading', false);
          commit('setChangeCutterModalVisibility', false);
        });
    },
    changeCrmPlanManager({ commit, state }, { id, managerId }) {
      commit('setChangeNinjaModalLoading', true);
      return api.changeManager(id, managerId, false)
        .then((resp) => resp.data)
        .finally(() => {
          commit('setCurrentPlanManager', state.simpleUsersList.find((item) => item.id === managerId));
          commit('setChangeManagerModalLoading', false);
          commit('setChangeManagerModalVisibility', false);
        });
    },
    changeCrmPlanManager3d({ commit, state }, { id, managerId }) {
      commit('setChangeNinjaModalLoading', true);
      return api.changeManager(id, managerId, true)
        .then((resp) => resp.data)
        .finally(() => {
          commit('setCurrentPlanManager', state.simpleUsersList.find((item) => item.id === managerId));
          commit('setChangeManagerModalLoading', false);
          commit('setChangeManagerModalVisibility', false);
        });
    },
    changeCrmPlanReviewer({ commit, state }, { id, reviewerId }) {
      commit('setChangeReviewerModalLoading', true);
      return api.changeReviewer(id, reviewerId, false)
        .then((resp) => resp.data)
        .finally(() => {
          commit('setCurrentPlanReviewer', state.simpleUsersList.find((item) => item.id === reviewerId));
          commit('setChangeReviewerModalLoading', false);
          commit('setChangeReviewerModalVisibility', false);
        });
    },
    changeCrmPlanReviewer3d({ commit, state }, { id, reviewerId }) {
      commit('setChangeReviewerModalLoading', true);
      return api.changeReviewer(id, reviewerId, true)
        .then((resp) => resp.data)
        .finally(() => {
          commit('setCurrentPlanReviewer', state.simpleUsersList.find((item) => item.id === reviewerId));
          commit('setChangeReviewerModalLoading', false);
          commit('setChangeReviewerModalVisibility', false);
        });
    },
    updateCrmPlanDeadline({ commit }, { id, deadlineAt }) {
      return api.updateCrmPlanDeadline(id, { deadline_at: deadlineAt })
        .then((resp) => {
          commit('setCurrentPlanDeadline', {
            deadlineAt,
            deadlineOverridden: true,
          });
          return resp.data;
        });
    },
    getTagsList(store, params) {
      return httpClient.get('/api/crm/tags/', { params }).then((resp) => {
        const { data } = resp;
        store.commit('setTagsList', data);
        return data;
      });
    },
    attachTagsToPlan(store, request) {
      return httpClient.post(`/api/crm/plans/${request.id}/tags`, request)
        .then((resp) => resp.data);
    },
    getStatusHistory({ commit }, { id, type }) {
      commit('setStatusHistoryLoading', true);

      return httpClient.get(`api/crm/plans/${id}/status_logs`, { params: { type } })
        .then(({ data }) => {
          commit('setStatusHistory', data);
          return data;
        })
        .finally(() => commit('setStatusHistoryLoading', false));
    },
    getSimpleUsersList({ state, commit }) {
      if (state.isSimpleUsersListLoading || state.simpleUsersList.length !== 0) {
        return state.simpleUsersList;
      }

      commit('setIsSimpleUsersListLoading', true);

      return httpClient.get('/api/users/').then(({ data }) => {
        commit('getSimpleUsersList', data);
        return data;
      }).finally(() => {
        commit('setIsSimpleUsersListLoading', false);
      });
    },
    getAdjustmentsByCrmPlanId({ commit }, { id }) {
      commit('setAdjustmentsLoading', true);
      return api.getAdjustmentsByCrmPlanId(id)
        .then(({ data }) => {
          commit('setAdjustments', data);
          return data;
        }).finally(() => commit('setAdjustmentsLoading', false));
    },
  },
  modules: {
    myPlans,
    apartments,
    statistic,
    plans,
    plan,
    customers,
  },
};
